import { UnreachableCaseError } from '@/utils/errors/unreachable-case-error';

import { FeApi } from '../..';
import { SiteMetadata } from '../../domain/entities/site-metadata';

type ApiSection = Extract<
  Awaited<ReturnType<typeof FeApi.customerInfosV1SitesMetadataRetrieve>>,
  { status: 200 }
>['data'][number];

// type of the content property of ApiSection
type ApiRow = Exclude<
  Exclude<ApiSection, undefined>['contents'],
  undefined
>[number][number];

type ApiFormat = Exclude<ApiRow['format'], undefined>;
type ApiSource = Exclude<ApiRow['source'], undefined>;

export function siteMetadataSectionMapper(
  data: ApiSection,
): SiteMetadata.Section {
  return {
    key: data.section ?? '',
    groups:
      data.contents?.map((content, i) => ({
        key: String(i),
        rows: content.map(siteMetadataRowMapper),
      })) ?? [],
  };
}

export function siteMetadataRowMapper(data: ApiRow): SiteMetadata.Row {
  // @ts-expect-error - attribute_name is always defined, api type should be fixed
  const key: string = data.attribute_name;

  const document = data.source
    ? siteMetadataDocumentMapper(data.source)
    : undefined;

  return {
    key,
    document,
    value: siteMetadataValueMapper(data.value, data.format),
  };
}

export function siteMetadataValueMapper(
  value: unknown,
  format?: ApiFormat,
): SiteMetadata.Value {
  if (format) {
    switch (format) {
      case 'ampere_hour':
      case 'watt':
      case 'watt_hour':
      case 'celsius':
      case 'percent': {
        if (typeof value !== 'number') {
          break;
        }

        return { type: 'number', format, value };
      }
      case 'date': {
        try {
          if (typeof value !== 'string') {
            break;
          }

          const date = new Date(value);
          return { type: 'date', format, value: date };
        } catch (_) {
          // it means we weren't able to parse the str as a date
          break;
        }
      }
      default: {
        new UnreachableCaseError(format);
      }
    }
  }

  // fallback
  if (typeof value === 'string') {
    return { type: 'string', value };
  }

  if (typeof value === 'number') {
    return {
      type: 'number',
      value,
    };
  }

  return {
    type: 'unknown',
    value,
  };
}

export function siteMetadataDocumentMapper(
  data: ApiSource,
): SiteMetadata.Document | undefined {
  if (!data.uuid || !data.name) {
    return undefined;
  }

  return {
    id: data.uuid,
    name: data.name,
  };
}
